<template>
  <section class="combo-page">
    <header class="combo-page__header">
      <div>
        <h1>{{ $t('combo_info') }}</h1>
        <p>{{ $t(hasActivePlan ? 'enterprise_ui_combo_upgrade_notice' : 'enterprise_ui_choose_plan') }}</p>
      </div>
    </header>

    <div v-if="hasActivePlan" class="combo-active">
      <section class="combo-plan-card">
        <div class="combo-plan-card__top">
          <div class="combo-plan-card__identity">
            <div>
              <div class="combo-plan-card__title-row">
                <strong>Doclingo</strong>
                <em>{{ isGlobalTeam ? $t('enterprise_ui_global_plan', { plan: getComboLevInfo() }) : getComboLevInfo() }}</em>
                <span>{{ `${String($t('combo_ordered_top_other_title')).trim()}${formatDate(bizTeamInfo.serviceEndTime)}` }}</span>
              </div>
            </div>
          </div>
          <button class="combo-outline-button" type="button" @click="openPlanDialog">{{ $t('enterprise_ui_upgrade_plan') }}</button>
        </div>

        <div class="combo-overview-grid" :class="{ 'is-global': isGlobalTeam }">
          <div class="combo-overview-item">
            <div class="combo-overview-item__label-row">
              <div class="combo-overview-item__label">{{ isGlobalTeam ? $t('enterprise_ui_global_seat_count') : $t('enterprise_ui_seat_count') }}</div>
              <div class="combo-overview-item__value"><strong>{{ Number(bizTeamInfo.memberCount || 0) }}</strong><span>/ {{ seatLimit }}</span></div>
            </div>
            <div class="combo-progress"><i :style="{ width: `${seatUsagePercent}%` }" /></div>
            <button class="combo-text-link" type="button" @click="openMemberManagement">{{ $t('enterprise_ui_manage_members') }} →</button>
          </div>
          <div v-if="!isGlobalTeam" class="combo-overview-item">
            <div class="combo-overview-item__label-row">
              <div class="combo-overview-item__label">{{ $t('combo_ordered_bottom_right_tip') }}</div>
              <div class="combo-overview-item__value"><strong>{{ formatQuota(quotaSnapshot.usedWords) }}</strong><span>/ {{ formatQuota(quotaSnapshot.totalWords) }}</span></div>
            </div>
            <div class="combo-progress"><i :style="{ width: `${quotaUsagePercent}%` }" /></div>
            <div class="combo-overview-item__footer">
              <span v-if="quotaSnapshot.packageTotalWords > 0">{{ $t('enterprise_ui_topup_total', { count: formatQuota(quotaSnapshot.packageTotalWords) }) }}</span>
              <button v-if="canManageBalanceAlert" class="combo-text-link" type="button" @click="openBalanceAlertDialog">{{ $t('enterprise_management_alert_settings') }} →</button>
            </div>
          </div>
        </div>
      </section>

      <div v-if="isGlobalTeam" class="combo-pools">
        <section class="combo-pool-card" :class="{ 'is-current': currentSite === 'VIP' }">
          <div class="combo-pool-card__heading">
            <span><WorkbenchIcon name="globe" :size="14" /><strong>{{ $t('enterprise_management_china_pool') }}</strong><small>doclingo.vip</small></span>
            <em>{{ formatQuota(getPoolUsed('VIP')) }} / {{ formatQuota(getPoolTotal('VIP')) }}</em>
          </div>
          <div class="combo-progress"><i :style="{ width: `${getPoolUsagePercent('VIP')}%` }" /></div>
          <div class="combo-pool-card__footer">
            <span>{{ $t('enterprise_ui_topup_remaining', { count: formatQuota(getPoolFuelRemaining('VIP')) }) }}</span>
            <button v-if="canManageBalanceAlert" type="button" @click="openBalanceAlertDialog">{{ $t('enterprise_management_alert_settings') }} →</button>
          </div>
        </section>
        <section class="combo-pool-card" :class="{ 'is-current': currentSite === 'AI' }">
          <div class="combo-pool-card__heading">
            <span><WorkbenchIcon name="globe" :size="14" /><strong>{{ $t('enterprise_management_international_pool') }}</strong><small>doclingo.ai</small></span>
            <em>{{ formatQuota(getPoolUsed('AI')) }} / {{ formatQuota(getPoolTotal('AI')) }}</em>
          </div>
          <div class="combo-progress"><i :style="{ width: `${getPoolUsagePercent('AI')}%` }" /></div>
          <div class="combo-pool-card__footer">
            <span>{{ $t('enterprise_ui_topup_remaining', { count: formatQuota(getPoolFuelRemaining('AI')) }) }}</span>
            <button v-if="canManageBalanceAlert" type="button" @click="openBalanceAlertDialog">{{ $t('enterprise_management_alert_settings') }} →</button>
          </div>
        </section>
      </div>

      <section class="combo-fuel-section">
        <div class="combo-section-heading">
          <div>
            <h2>{{ $t('combo_fuel_info_title') }}</h2>
          </div>
        </div>
        <div class="combo-fuel-list">
          <ProFuelBagItem :isCombo="true" :pricingMode="isGlobalTeam ? 'global' : 'local'" />
        </div>
      </section>
    </div>

    <section v-else class="combo-unordered">
      <div class="combo-unordered__heading">
        <span class="combo-unordered__icon"><WorkbenchIcon name="package" :size="24" /></span>
        <div>
          <h2>{{ $t('pro_business_title') }}</h2>
          <p>{{ $t('pro_business_sub_title') }}</p>
        </div>
      </div>
      <div class="combo-unordered-list">
        <ProSubscribeItem />
      </div>
    </section>
  </section>
</template>

<script setup lang="ts">
import AccountServer from '~/services/AccountServer'
import GlobalBizServer from '~/services/GlobalBizServer'
import { type FileItem } from '~/services/AccountServer/types'
import type { GlobalBizSite, GlobalBizSpaceSummary } from '~/services/GlobalBizServer/types'
import EventBus from 'vue3-eventbus'
import { NetCode } from '~/types/const'
import useUserStore from '~/store/modules/user'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import ProSubscribeItem from '~/components/Pro/ProSubscribe/ProSubscribeItem.vue'
import ProFuelBagItem from '~/components/Pro/ProFuelBag/ProFuelBagItem.vue'
import { isExcelTranslationExtension } from '@/utils/translationFileType'
import WorkbenchIcon from '@/components/Workbench/WorkbenchIcon.vue'
import type ProDialog from '~/components/Pro/ProDialog/ProDialog.vue'
import { useSiteConfig } from '~/config/useSiteConfig'
import { getEnterpriseFuelMode, getEnterpriseQuotaSnapshot } from '~/utils/enterpriseTrade'
import { getEnterpriseManagementAccess } from '~/utils/enterpriseManagement'
const tableData = ref<FileItem[]>([])
const userStore = useUserStore()
const { userInfo, bizTeamInfo, userRights } = storeToRefs(userStore)
const {t} = useI18n()
const proDialogRef = inject('proDialogRef') as Ref<InstanceType<typeof ProDialog>>
const { isVipSite } = useSiteConfig()
const router = useRouter()
const route = useRoute()
const currentSite = computed(() => isVipSite.value ? 'VIP' : 'AI')
const globalSpaces = ref<GlobalBizSpaceSummary[]>([])
const enterpriseBalanceAlert = useEnterpriseBalanceAlert()
const hasActivePlan = computed(() => Number(bizTeamInfo.value?.serviceLevel || 0) > 0)
const isGlobalTeam = computed(() => getEnterpriseFuelMode(bizTeamInfo.value) === 'global')
const canManageBalanceAlert = computed(() => getEnterpriseManagementAccess(
  bizTeamInfo.value,
  userInfo.value?.bizTeamId,
  userInfo.value?.bizTeamMemberRole,
).isManager && getEnterpriseManagementAccess(
  bizTeamInfo.value,
  userInfo.value?.bizTeamId,
  userInfo.value?.bizTeamMemberRole,
).hasActivePlan)
const seatLimit = computed(() => Number(
  (isGlobalTeam.value ? bizTeamInfo.value?.globalSeatLimit : userRights.value?.memberCountLimit) || 0,
))
const seatUsagePercent = computed(() => percent(Number(bizTeamInfo.value?.memberCount || 0), seatLimit.value))
const quotaSnapshot = computed(() => getEnterpriseQuotaSnapshot(bizTeamInfo.value))
const quotaUsagePercent = computed(() => percent(quotaSnapshot.value.usedWords, quotaSnapshot.value.totalWords))
const serviceTotal = computed(() => quotaSnapshot.value.serviceTotalWords)
const serviceRemaining = computed(() => Number(bizTeamInfo.value?.remainServiceWords || 0))
const fuelRemaining = computed(() => Number(bizTeamInfo.value?.remainPackageWords || 0))
const params = {
  pageSize: 4,
  pageNo: 1,
  pageTotalSize: 0,
  recordIds: null as number[] | null,
  prevPageId: null as number | null,
  nextPageId: null as number | null
}

watch(userInfo, ()=>{
  getEnterpriseInfo()
})

watch(
  () => [isGlobalTeam.value, bizTeamInfo.value?.globalContractNo] as const,
  ([globalTeam, contractNo]) => {
    if (!globalTeam || !contractNo) {
      globalSpaces.value = []
      return
    }
    void loadGlobalSpaces(contractNo)
  },
  { immediate: true },
)

function percent(value: number, total: number) {
  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) return 0
  return Math.min(100, Math.max(0, value / total * 100))
}

function formatQuota(value: number | null | undefined) {
  const amount = Number(value ?? 0)
  if (!Number.isFinite(amount)) return '—'
  return amount.toLocaleString('en-US')
}

async function loadGlobalSpaces(contractNo: string) {
  try {
    const response = await GlobalBizServer.members(contractNo)
    if (response.code === NetCode.SUCCESS) globalSpaces.value = response.data?.spaces || []
  } catch {
    globalSpaces.value = []
  }
}

function getPoolSummary(site: GlobalBizSite) {
  return globalSpaces.value.find(item => item.region === site)
}

function getPoolTotal(site: GlobalBizSite) {
  const total = Number(getPoolSummary(site)?.totalQuota)
  if (Number.isFinite(total)) return total
  return site === currentSite.value ? serviceTotal.value : Number.NaN
}

function getPoolRemaining(site: GlobalBizSite) {
  const remaining = Number(getPoolSummary(site)?.remainQuota)
  if (Number.isFinite(remaining)) return remaining
  return site === currentSite.value ? serviceRemaining.value : Number.NaN
}

function getPoolUsed(site: GlobalBizSite) {
  const total = getPoolTotal(site)
  const remaining = getPoolRemaining(site)
  if (!Number.isFinite(total) || !Number.isFinite(remaining)) return Number.NaN
  return Math.max(0, total - remaining)
}

function getPoolFuelRemaining(site: GlobalBizSite) {
  return site === currentSite.value ? fuelRemaining.value : Number.NaN
}

function getPoolUsagePercent(site: GlobalBizSite) {
  return percent(getPoolUsed(site), getPoolTotal(site))
}

function openPlanDialog() {
  proDialogRef.value?.open({ mode: 0, pricingMode: isGlobalTeam.value ? 'global' : 'local' })
}

function openMemberManagement() {
  router.push({ query: { ...route.query, menu: 'enterprise' } })
}

function openBalanceAlertDialog() {
  enterpriseBalanceAlert.openDialog()
}

const getComboLevInfo = () => {
  const serviceLevel = Number(bizTeamInfo.value.serviceLevel || 0)
  const globalPlanCode = String(bizTeamInfo.value.globalPlanCode || '').toUpperCase()
  if (isGlobalTeam.value && (globalPlanCode === 'BASE' || serviceLevel === 5)) {
    return t('combo_ordered_top_base')
  } else if (isGlobalTeam.value && (globalPlanCode === 'PROFESSIONAL' || serviceLevel === 6)) {
    return t('combo_ordered_top_middle')
  } else if (isGlobalTeam.value && (globalPlanCode === 'ADVANCED' || serviceLevel === 7)) {
    return t('combo_ordered_top_high')
  } else if (serviceLevel === 1) {
    return t('combo_ordered_top_base')
  } else if (serviceLevel === 2) {
    return t('combo_ordered_top_middle')
  } else if (serviceLevel === 3) {
    return t('combo_ordered_top_high')
  }
  return t('pro_business_subscribe_item_title4')
}

// 动态计算是否启用折叠（即返回 3）
const getPagerCount = computed(() => {
  return params.pageTotalSize > 3 ? 3 : params.pageTotalSize
})

const handleCurrentChange = (changeValue) => {
  params.pageNo = changeValue;
  requestFileList(false, true)
  document.body.scrollTo({ top: 0, behavior: 'smooth' });
}

const handleSizeChange = (sizeValue) => { 
  params.pageNo = sizeValue
  requestFileList(false, true)
  document.body.scrollTo({ top: 0, behavior: 'smooth' });
}

// 获取企业人员list
function requestFileList(isInit = false) {
  let loading;
  let listDom = document.getElementById("fileUseList")
  if (listDom) {
    loading = ElLoading.service({
        lock: true,
        target: listDom
    })
  }
  console.log('loading-====?>>>>', loading)
  let param = {
    pageSize: 4,
    pageNo: params.pageNo,
    operateStatus: 1
  }
  if (params.recordIds) {
    param.recordIds = params.recordIds
  }
  param["modeType"] = 0;
//   eventBus.emit('updateHomePage')

  AccountServer.getRequestAccountUseFilePageList(param).then((res) => {
    console.log('res===>>', res)
    if (res.code == "1") {
      let list = res.dataList
      if (list && list.length > 0) {

        list.forEach((item) => {
          if (item.operateTag) {
            item.operateTag = item.operateTag ? JSON.parse(item.operateTag) : item.operateTag
          }
          if (item.remark) {
            item.remark = item.remark ? JSON.parse(item.remark) : item.remark
          }
          if (item.transReqJson) {
            item.transReqJson = item.transReqJson ? JSON.parse(item.transReqJson) : item.transReqJson
          }
        })
        params.pageTotalSize = res.totalPageCount ?? 0;
        tableData.value = list
      } else {
        tableData.value = [];
      }
      loading && loading.close()
    }
    
  }).catch((error) => {
    Toast(error.message);
    loading && loading.close()
  })
}

function getEnterpriseInfo(){
  // 被邀请
  if (userInfo.value && userInfo.value.bizTeamId) {
    requestFileList()
  }
}

function getFileType(item) {
  let fileString = item.origFileName.split('.').reverse()[0].toLowerCase()
  if(fileString == 'epub') {
    return "icon-icon_epub5"
  } else if (fileString == 'zip') {
    return "icon-icon_zip5"
  } else if (fileString == 'srt') {
    return "icon-icon_srt5"
  } else if (isExcelTranslationExtension(fileString)) {
    return "icon-icon_xlsl5"
  } else if (fileString == 'rar') {
    return "icon-icon_rar5"
  } else if (fileString == 'docx') {
    return "icon-icon_word5"
  } else if (fileString == 'pptx') {
    return "icon-icon_ppt5"
  } else if (fileString == 'pdf') {
    return "icon-icon_pdf5"
  } else if (fileString == 'png' || fileString == 'PNG') {
    return "icon-icon_png5"
  } else if (fileString == 'jpeg' || fileString == 'JPEG') {
    return "icon-icon_jpeg5"
  } else if (fileString == 'jpg' || fileString == 'JPG') {
    return "icon-icon_jpg5"
  } else if (fileString == 'txt') {
    return "icon-icon_txt5"
  }
  return ""
}

function handleOperation(file: FileItem) {
  // if (operation == t('account_enterprise_permission_edit')) {
  //   // 编辑成员
  //   createDialogRef.value?.open(2, member)
  // }else if (operation == t('account_enterprise_operation_delete')) {
  //   // 删除成员

  // }
}

onUnmounted(()=>{
  EventBus.off('createAccountEnterprise', getEnterpriseInfo)
})

onMounted(()=>{
  getEnterpriseInfo()
  EventBus.on('createAccountEnterprise', getEnterpriseInfo)
})

</script>

<style lang="scss" scoped>
.combo-page {
  display: flex;
  min-width: 0;
  max-width: 100%;
  flex-direction: column;
  gap: 18px;
}

.combo-page__header {
  min-height: 44px;

  h1 {
    margin: 0;
    color: #1f2329;
    font-size: 14px;
    font-weight: 800;
    line-height: 20px;
  }

  p {
    width: 100%;
    margin: 5px 0 0;
    color: #8f959e;
    font-size: 12.5px;
    line-height: 1.5;
  }
}

.combo-active {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 18px;
}

.combo-plan-card,
.combo-unordered {
  background: #fff;
  border: 1px solid #e8ebf0;
  border-radius: 12px;
}

.combo-plan-card {
  padding: 20px;
}

.combo-plan-card__top {
  display: flex;
  min-height: 34px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.combo-plan-card__identity {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.combo-plan-card__title-row {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  gap: 7px;

  strong {
    max-width: 300px;
    overflow: hidden;
    color: #1f2329;
    font-size: 15px;
    font-weight: 800;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  em {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 10px;
    color: #fff;
    font-size: 11px;
    font-style: normal;
    font-weight: 700;
    background: #2b7de9;
    border-radius: 10px;
  }

  > span {
    color: #8f959e;
    font-size: 12px;
    font-weight: 400;
  }
}

.combo-outline-button {
  height: 34px;
  padding: 0 14px;
  color: #2b7de9;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  background: #fff;
  border: 1px solid #2b7de9;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: #f5f9ff;
  }
}

.combo-overview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
  margin-top: 14px;

  &.is-global {
    grid-template-columns: 1fr;
  }
}

.combo-overview-item {
  display: flex;
  min-width: 0;
  min-height: 57px;
  flex-direction: column;
}

.combo-overview-item__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.combo-overview-item__label {
  color: #51565d;
  font-size: 12.5px;
  line-height: 18px;
}

.combo-overview-item__value {
  display: flex;
  align-items: baseline;
  gap: 3px;

  strong {
    color: #2b7de9;
    font-size: 13px;
    font-weight: 700;
  }

  span {
    color: #2b7de9;
    font-size: 13px;
    font-weight: 700;
  }
}

.combo-overview-item > .combo-progress {
  margin-top: 8px;
}

.combo-overview-item__footer {
  display: flex;
  min-height: 17px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  color: #8f959e;
  font-size: 11.5px;
  line-height: 17px;
}

.combo-text-link {
  margin-left: auto;
  padding: 0;
  color: #2b7de9;
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  line-height: 17px;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.combo-overview-item > .combo-text-link {
  align-self: flex-end;
  margin-top: 6px;
}

.combo-progress {
  width: 100%;
  height: 6px;
  overflow: hidden;
  background: #e4edf8;
  border-radius: 999px;

  i {
    display: block;
    height: 100%;
    background: #2b7de9;
    border-radius: inherit;
  }
}

.combo-pools {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.combo-pool-card {
  min-height: 95px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #e8ebf0;
  border-radius: 12px;

  &.is-current {
    border-color: #cfe2fb;
  }

  > .combo-progress {
    margin-top: 8px;
  }
}

.combo-pool-card__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  span {
    display: flex;
    align-items: center;
    gap: 5px;

    strong {
      color: #1f2329;
      font-size: 13.5px;
      font-weight: 800;
    }

    small {
      color: #8f959e;
      font-size: 11.5px;
    }
  }

  em {
    color: #2b7de9;
    font-size: 13px;
    font-style: normal;
    font-weight: 700;
  }
}

.combo-pool-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 7px;
  color: #8f959e;
  font-size: 11.5px;

  button {
    padding: 0;
    color: #2b7de9;
    font: inherit;
    font-weight: 600;
    background: transparent;
    border: 0;
    cursor: pointer;
  }
}

.combo-section-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  h2 {
    margin: 0;
    color: #1f2329;
    font-size: 14px;
    font-weight: 800;
  }

  p {
    margin: 4px 0 0;
    color: #98a2b3;
    font-size: 11px;
  }
}

.combo-fuel-list {
  margin-top: 18px;

  :deep(.pro-bag) {
    display: grid !important;
    width: 100% !important;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 14px !important;
  }

  :deep(.bg-item) {
    box-sizing: border-box;
    width: auto !important;
    min-height: 158px !important;
    align-items: center;
    gap: 10px;
    padding: 18px 16px !important;
    border-color: #e8ebf0 !important;
    border-radius: 12px !important;
  }

  :deep(.bag-heading) {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  :deep(.bag-title) {
    color: #1f2329 !important;
    font-size: 15px !important;
    font-weight: 800;
    line-height: 20px;
    text-align: left;
  }

  :deep(.bag-price) {
    width: 100%;
    min-height: 27px;
    align-items: center;
    justify-content: center;
    margin-top: 0 !important;
  }

  :deep(.bag-title-price) {
    color: #2b7de9 !important;
    margin-bottom: 0 !important;
    font-size: 22px !important;
    font-weight: 800 !important;
    line-height: 27px !important;
  }

  :deep(.bag-price-original) {
    font-size: 10px !important;
  }

  :deep(.bag-unit-price) {
    width: 100%;
    color: #8f959e;
    font-size: 11px;
    line-height: 13px;
    text-align: center;
  }

  :deep(.bag-item-btn) {
    height: 32px !important;
    margin-top: 0 !important;
    border-color: #2b7de9 !important;
    border-radius: 8px !important;
  }

  :deep(.bag-item-btn-title) {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    color: #2b7de9 !important;
    font-size: 12.5px !important;
    font-weight: 600;
    line-height: 1;
  }

  :deep(.discount) {
    position: static !important;
    width: auto !important;
    min-width: 0 !important;
    height: auto !important;
    flex: none;
    background: #e5484d !important;
    border-radius: 4px !important;
    align-items: center;
    justify-content: center;
  }

  :deep(.discountItem) {
    width: auto !important;
    min-width: 0 !important;
    height: auto !important;
    padding: 2px 6px !important;
  }

  :deep(.discountItemTitle) {
    width: auto;
    height: auto !important;
    font-size: 9.5px !important;
    line-height: 14px;
    text-align: center;
    white-space: nowrap;
  }
}

.combo-unordered {
  min-width: 0;
  padding: 16px;
}

.combo-unordered__heading {
  display: flex;
  align-items: center;
  gap: 10px;

  h2 {
    margin: 0;
    color: #1f2329;
    font-size: 16px;
    font-weight: 750;
  }

  p {
    margin: 4px 0 0;
    color: #98a2b3;
    font-size: 11px;
  }
}

.combo-unordered__icon {
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  color: #2b7de9;
  background: #edf5ff;
  border-radius: 10px;
}

.combo-unordered-list {
  min-width: 0;
  margin-top: 16px;

  :deep(.pro-subscribe) {
    min-height: 0 !important;
    margin: 0 !important;
  }

  :deep(.pro-subscribe-list) {
    display: grid !important;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px !important;
  }

  :deep(.pro-subscribe-list-item) {
    box-sizing: border-box;
    width: auto !important;
    min-width: 0;
    height: auto !important;
    min-height: 460px;
    padding: 16px !important;
    border-color: #e8ebf0 !important;
    border-radius: 8px !important;
  }

  :deep(.item-top .title) {
    color: #1f2329 !important;
    font-size: 16px !important;
    font-weight: 700;
  }

  :deep(.title_desc),
  :deep(.title_desc_customized) {
    width: 100% !important;
    height: 42px !important;
    margin-top: 7px !important;
  }

  :deep(.pro-subscribe-subTitle) {
    font-size: 10.5px !important;
    line-height: 15px !important;
  }

  :deep(.priceItem) {
    height: 50px !important;
    margin: 12px 0 !important;
  }

  :deep(.item-char-title) {
    font-size: 17px !important;
  }

  :deep(.item-pdf-title) {
    font-size: 10px !important;
  }

  :deep(.item-price),
  :deep(.item-customized) {
    height: 34px !important;
    margin-top: 12px !important;
    border-color: #2b7de9 !important;
    border-radius: 7px !important;
  }

  :deep(.item-price-title),
  :deep(.item-customized-title) {
    color: #2b7de9 !important;
    font-size: 12px !important;
    font-weight: 600;
  }

  :deep(.freeLine) {
    width: 100% !important;
    margin: 14px 0 !important;
    background: #e8ebf0 !important;
  }

  :deep(.tipsItem) {
    margin-bottom: 9px !important;
  }

  :deep(.tipsItem .title) {
    min-width: 0;
    color: #51565d !important;
    font-size: 10.5px !important;
    line-height: 15px !important;
  }

  :deep(.tipsItem .iconfont) {
    width: 12px !important;
    height: 12px !important;
    font-size: 12px !important;
  }
}

@media (max-width: 900px) {
  .combo-overview-grid,
  .combo-pools {
    grid-template-columns: 1fr;
  }

  .combo-unordered-list :deep(.pro-subscribe-list) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
